import { API } from '@hero/api';
import {
  Account,
  AccountTransaction,
  accountStatusSchema,
  Currency,
  Id,
  objectIdSchema,
  paginationSchema,
  paginationWithSearchSchema,
} from '@hero/schema';
import { z } from 'zod';

// ─────────────────────────── admin ───────────────────────────

/**
 * Admin: list bilateral `Account` documents touching a party.
 *
 * The party is the org you're looking at (`partyId`). The match
 * goes against `Account.partyIds` (multikey index), so the same
 * call surfaces every counterparty that org trades with — either
 * direction. Frontend tells which side the viewer is on via
 * `getRoleForViewer` / `getViewerBalance` from `AccountService`.
 *
 * @see docs/relations-v2/10-account-and-account-transactions.md
 */
export const accountGetAllAdminAPISchema = paginationSchema.extend({
  /** the party (org/client) the accounts must include */
  partyId: objectIdSchema,
  /** optional currency filter */
  currency: z.string().optional(),
  /** optional status filter — matches the underlying `Account.status` enum */
  status: accountStatusSchema.optional(),
});
export type AccountGetAllAdminAPI = API<
  z.infer<typeof accountGetAllAdminAPISchema>,
  Account[]
>;

/**
 * Admin: get a single `Account` by id.
 *
 * Used by the `AccountPage` (per-pair drilldown) and the
 * `OrgCRMAccountsTab`.
 */
export const accountGetOneAdminAPISchema = z.object({
  _id: objectIdSchema,
});
export type AccountGetOneAdminAPI = API<
  z.infer<typeof accountGetOneAdminAPISchema>,
  Account
>;

/**
 * Admin: list `AccountTransaction` documents for an account (or for
 * a specific invoice on that account).
 *
 * The `invoiceId` filter is the bilateral equivalent of the
 * legacy `Transaction.invoices[]._id` filter — used by the
 * `InvoiceDrawer > Transactions` tab.
 */
export const accountTransactionGetAllAdminAPISchema = paginationSchema.extend({
  /** the account whose movements we want */
  accountId: objectIdSchema.optional(),
  /** restrict to rows that touched this invoice */
  invoiceId: objectIdSchema.optional(),
  /** restrict to a specific party (matches `account.parties[]._id`) */
  partyId: objectIdSchema.optional(),
  /** filter by transaction type */
  type: z.string().optional(),
});
export type AccountTransactionGetAllAdminAPI = API<
  z.infer<typeof accountTransactionGetAllAdminAPISchema>,
  AccountTransaction[]
>;

// ─────────────────────── current user (party) ───────────────────────

/**
 * Party-facing: list bilateral `Account` documents where the
 * viewer is one of the two parties.
 *
 * Same shape as the admin call minus the explicit `partyId` (the
 * viewer's org is derived from the auth token by the controller).
 */
export const accountGetCurrentUserAPISchema = paginationWithSearchSchema.extend({
  /** optional currency filter */
  currency: z.string().optional(),
  /** optional status filter */
  status: accountStatusSchema.optional(),
});
export type AccountGetCurrentUserAPI = API<
  z.infer<typeof accountGetCurrentUserAPISchema>,
  Account[]
>;

/**
 * Party-facing: get a single `Account` by id.
 *
 * The backend asserts the viewer is on `account.partyIds[]`; 403
 * otherwise.
 */
export const accountGetOneCurrentUserAPISchema = z.object({
  _id: objectIdSchema,
});
export type AccountGetOneCurrentUserAPI = API<
  z.infer<typeof accountGetOneCurrentUserAPISchema>,
  Account
>;

/**
 * Party-facing: exact-pair lookup. Returns the bilateral
 * `Account` document for the unordered pair
 * `(partyAId, partyBId)` + currency, or `null` if no document
 * has been created yet.
 *
 * Used by the CRM `Invoices` tab to resolve the
 * viewer-vs-counterparty balance without creating an empty
 * Account doc on every CRM open.
 */
export const accountGetByPairAPISchema = z.object({
  /** one of the two parties — order doesn't matter */
  partyAId: objectIdSchema,
  /** the other party */
  partyBId: objectIdSchema,
  /** currency the account is held in; defaults to the org's currency on the backend */
  currency: z.string().optional(),
});
export type AccountGetByPairAPI = API<
  z.infer<typeof accountGetByPairAPISchema>,
  Account | null
>;

/**
 * Party-facing: list `AccountTransaction` rows scoped to the
 * viewer's accounts. The backend AND-s the filters with the
 * "viewer must be on the account" gate.
 */
export const accountTransactionGetCurrentUserAPISchema = paginationWithSearchSchema.extend({
  /** the account whose movements we want */
  accountId: objectIdSchema.optional(),
  /** restrict to rows that touched this invoice */
  invoiceId: objectIdSchema.optional(),
  /** restrict to a specific party (matches `account.parties[]._id`) */
  partyId: objectIdSchema.optional(),
  /** filter by transaction type */
  type: z.string().optional(),
});
export type AccountTransactionGetCurrentUserAPI = API<
  z.infer<typeof accountTransactionGetCurrentUserAPISchema>,
  AccountTransaction[]
>;

/**
 * Party-facing: get a single `AccountTransaction` by id. The
 * backend asserts the viewer is on the associated account.
 */
export const accountTransactionGetOneCurrentUserAPISchema = z.object({
  _id: objectIdSchema,
});
export type AccountTransactionGetOneCurrentUserAPI = API<
  z.infer<typeof accountTransactionGetOneCurrentUserAPISchema>,
  AccountTransaction
>;

// Convenience re-exports for backends/SDK call sites that prefer
// the existing `Account` / `Currency` / `Id` types.
export type { Account, AccountTransaction, Currency, Id };
